import {
  ReturnFlow,
  Step,
} from "@redotech/redo-return-core-common/return-flow";
import {
  FlowElementTextOverlay,
  FlowElementTranslatableField,
} from "@redotech/redo-return-core-common/return-flow/flow-element-translation";
import { createContext, useCallback, useContext } from "react";
import { localizeCustomerPortalFlowText } from "./util";

/** The empty default falls every lookup through to the built-in localization. */
export const FlowElementTextOverlayContext =
  createContext<FlowElementTextOverlay>({});

/**
 * Display-only, per the invariant on FlowElementTextOverlay: the merchant's
 * translation, else the Redo-default localization, else the English text.
 */
export function flowElementDisplayText(
  overlay: FlowElementTextOverlay,
  elementId: string | undefined,
  field: FlowElementTranslatableField,
  text: string,
): string {
  const translated = elementId ? overlay[elementId]?.[field] : undefined;
  return translated || localizeCustomerPortalFlowText(text);
}

export function collectFlowElementIds(
  flows: (ReturnFlow | undefined)[],
): string[] {
  const ids = new Set<string>();
  for (const flow of flows) {
    for (const step of flow?.steps ?? []) {
      if (step.type !== Step.MULTIPLE_CHOICE) {
        continue;
      }
      if (step.stepUUID) {
        ids.add(step.stepUUID);
      }
      for (const option of step.options) {
        if (option.optionUUID) {
          ids.add(option.optionUUID);
        }
      }
    }
  }
  return [...ids];
}

export function useFlowElementText(): (
  elementId: string | undefined,
  field: FlowElementTranslatableField,
  text: string,
) => string {
  const overlay = useContext(FlowElementTextOverlayContext);
  return useCallback(
    (elementId, field, text) =>
      flowElementDisplayText(overlay, elementId, field, text),
    [overlay],
  );
}
